import type { DetailedReportDay, SummaryReportGroup } from 'api/types/reports.types';
import dayjs from 'dayjs';
import type { BarChartDatum, BarDef, ChartTooltipRow } from 'shared/components/charts';
import { BILLABLE_COLOR, NON_BILLABLE_COLOR, secondsToHMS } from 'shared/components/charts';
import { formatDateRange, getDateRangeShortcut, getWeekForDate } from 'shared/utils/dateUtils';
import type { DateRange } from 'shared/utils/dateUtils';

import type { ReportFiltersState } from '../types/reports.types';

import type { SummaryRow, WeeklyTableRow, WeeklyUserRow } from './exportHelpers';

export type { DateRange };
export { formatDateRange, getDateRangeShortcut, getWeekForDate };

export function getDefaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return date.toISOString().split('T')[0];
}

export function getDefaultEndDate(): string {
  const date = new Date();
  return date.toISOString().split('T')[0];
}

export function hasActiveFilters(filters: ReportFiltersState): boolean {
  return (
    !!filters.userIds?.length ||
    !!filters.clientIds?.length ||
    !!filters.projectIds?.length ||
    filters.billable === true ||
    filters.billable === false ||
    filters.hasDescription === false ||
    !!filters.descriptionSearch
  );
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export function formatPercentage(seconds: number, totalDuration: number): string {
  if (totalDuration === 0) return '0.00%';
  return ((seconds / totalDuration) * 100).toFixed(2) + '%';
}

export function formatAmount(cents: number): string {
  return `${(cents / 100).toFixed(2)} USD`;
}

// Detailed report data transformation utilities
export interface DetailedEntry {
  id: string;
  userId: string;
  projectId?: string;
  clientId?: string;
  description?: string;
  isBillable: boolean;
  date: string;
  startedAt: string;
  endedAt?: string;
  durationSecs: number;
  amountCents: number;
  userName?: string;
  userEmail?: string;
  projectName?: string;
  projectColor?: string;
  clientName?: string;
}

export interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

export function flattenDetailedReportData(
  days: DetailedReportDay[],
  userMap: Map<string, string>,
  projectMap: Map<string, { name: string; color: string }>,
  clientMap: Map<string, string>,
  emailMap: Map<string, string> = new Map(),
): DetailedEntry[] {
  const flattened: DetailedEntry[] = [];
  days.forEach((day) => {
    day.entries.forEach((item) => {
      const project = projectMap.get(item.projectId || '') || {
        name: 'Unknown Project',
        color: '#ccc',
      };
      const clientName = item.clientId ? clientMap.get(item.clientId) : undefined;
      flattened.push({
        ...item,
        id: item.timeEntryId,
        date: day.date,
        description: item.description || '',
        userName: userMap.get(item.userId) || 'Unknown User',
        userEmail: emailMap.get(item.userId) || '',
        projectName: project.name,
        projectColor: project.color,
        clientName,
      });
    });
  });
  return flattened;
}

export function sortDetailedEntries(
  entries: DetailedEntry[],
  sortConfig: SortConfig,
): DetailedEntry[] {
  const { column, direction } = sortConfig;
  return [...entries].sort((a, b) => {
    let comparison = 0;
    if (column === 'startedAt') {
      comparison = new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
    } else if (column === 'durationSecs') {
      comparison = a.durationSecs - b.durationSecs;
    } else if (column === 'description') {
      comparison = (a.description || '').localeCompare(b.description || '');
    } else if (column === 'userName') {
      comparison = (a.userName || '').localeCompare(b.userName || '');
    } else if (column === 'amountCents') {
      comparison = a.amountCents - b.amountCents;
    }
    return direction === 'asc' ? comparison : -comparison;
  });
}

// Summary report data transformation utilities
export function resolveGroupLabel(
  field: string,
  key: string | null,
  projects: Array<{ id: string; name: string }> = [],
  clients: Array<{ id: string; name: string }> = [],
  users: Array<{ id: string; name: string }> = [],
): string {
  if (key === null || key === '') {
    if (field === 'Description') return '(No description)';
    return `(No ${field.toLowerCase()})`;
  }
  switch (field) {
    case 'Project':
      return projects.find((p) => p.id === key)?.name ?? 'Unknown Project';
    case 'Client':
      return clients.find((c) => c.id === key)?.name ?? 'Unknown Client';
    case 'User':
      return users.find((u) => u.id === key)?.name ?? 'Unknown User';
    case 'Description':
      return key || '(No description)';
    case 'Week':
      return `Week of ${key}`;
    default:
      return key;
  }
}

export function transformSummaryData(
  groups: SummaryReportGroup[],
  projects: Array<{ id: string; name: string; color: string }>,
  clients: Array<{ id: string; name: string }>,
  users: Array<{ id: string; name: string }> = [],
): SummaryRow[] {
  return groups.map((group, index) => {
    const displayName = resolveGroupLabel(group.field, group.key, projects, clients, users);
    const color =
      group.field === 'Project'
        ? (projects.find((p) => p.id === group.key)?.color ?? '#ccc')
        : '#ccc';
    return {
      id: `${group.field}-${group.key ?? 'null'}-${index}`,
      projectId: group.field === 'Project' ? (group.key ?? '') : '',
      clientId: group.field === 'Client' ? (group.key ?? '') : '',
      userId: group.field === 'User' ? (group.key ?? '') : '',
      entityKey: group.key ?? '',
      projectName: displayName,
      clientName: '',
      groupField: group.field,
      color,
      totalDurationSecs: group.totalDurationSecs,
      billableDurationSecs: group.billableDurationSecs,
      amountCents: group.amountCents,
      entryCount: group.entryCount,
      isBillable: group.billableDurationSecs > 0,
      subGroups: group.subGroups ?? undefined,
    };
  });
}

export function sortSummaryRows(rows: SummaryRow[], sortConfig: SortConfig): SummaryRow[] {
  const { column, direction } = sortConfig;
  return [...rows].sort((a, b) => {
    let comparison = 0;
    if (column === 'duration') {
      comparison = a.totalDurationSecs - b.totalDurationSecs;
    } else if (column === 'amount') {
      comparison = a.amountCents - b.amountCents;
    } else if (column === 'title') {
      comparison = a.projectName.localeCompare(b.projectName);
    }
    return direction === 'asc' ? comparison : -comparison;
  });
}

// Weekly report data transformation utilities
export function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  let cursor = dayjs(startDate);
  while (!cursor.isAfter(dayjs(endDate))) {
    dates.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
  return dates;
}

export function transformWeeklyData(
  groups: any[],
  projects: Array<{ id: string; name: string; color: string }>,
  clients: Array<{ id: string; name: string }>,
  users: Array<{ id: string; name: string }> = [],
): WeeklyTableRow[] {
  return groups.map((group) => {
    const project = projects.find((p) => p.id === group.key);
    const client = clients.find((c) => c.id === group.clientId);

    const userRows: WeeklyUserRow[] = (group.subGroups ?? []).map((sg: any) => {
      const user = users.find((u) => u.id === sg.key);
      return {
        userId: sg.key ?? undefined,
        userName: user?.name ?? 'Unknown User',
        dailyDurations: sg.dailyDurationSecs ?? {},
        totalDurationSecs: sg.totalDurationSecs,
      };
    });

    return {
      id: group.key ?? `no-project-${group.clientId ?? 'none'}`,
      projectName: project?.name ?? 'Unknown Project',
      clientName: client?.name ?? '',
      color: project?.color ?? '#ccc',
      dailyDurations: group.dailyDurationSecs ?? {},
      totalDurationSecs: group.totalDurationSecs,
      userRows,
    };
  });
}

// ── Chart tooltip row builders ────────────────────────────────────────────────

export function buildBillabilityTooltipRows(
  datum: BarChartDatum,
  totalSecs: number,
): ChartTooltipRow[] {
  const billableSecs: number = (datum._billableSecs as number) ?? 0;
  const nonBillableSecs = totalSecs - billableSecs;
  const pct = (secs: number) =>
    totalSecs > 0 ? `${((secs / totalSecs) * 100).toFixed(2)}%` : '0.00%';
  return [
    { color: BILLABLE_COLOR, label: 'Billable', duration: secondsToHMS(billableSecs), percentage: pct(billableSecs) },
    { color: NON_BILLABLE_COLOR, label: 'Non-billable', duration: secondsToHMS(nonBillableSecs), percentage: pct(nonBillableSecs) },
  ].filter((r) => r.duration !== '00:00:00');
}

export function buildProjectTooltipRows(
  datum: BarChartDatum,
  projectBars: BarDef[],
  totalSecs: number,
  limit?: number,
): { rows: ChartTooltipRow[]; overflow: number } {
  const pct = (secs: number) =>
    totalSecs > 0 ? `${((secs / totalSecs) * 100).toFixed(2)}%` : '0.00%';
  const all = projectBars
    .map((bar) => {
      const secs = Math.round(((datum[bar.dataKey] as number) ?? 0) * 3600);
      return { color: bar.color, label: bar.label, duration: secondsToHMS(secs), percentage: pct(secs), secs };
    })
    .filter((r) => r.secs > 0)
    .sort((a, b) => b.secs - a.secs);
  const visible = limit ? all.slice(0, limit) : all;
  return {
    rows: visible.map(({ secs: _, ...rest }) => rest),
    overflow: all.length - visible.length,
  };
}
